

import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import { useUser } from '@/contexts/UserContext';
import { useShow } from '@/contexts/ShowContext';
import { toast } from 'sonner';
import { apiFetch, getAuthToken } from '@/lib/api';
import { config } from '@/lib/config';

const WebSocketContext = createContext(null);

const WS_URL = config.wsUrl;
const WS_RECONNECT_BASE_DELAY = 1000;
const WS_RECONNECT_MAX_DELAY = 30000;

export function WebSocketProvider({ children }) {
  const { user } = useUser();
  const { show } = useShow();
  const wsRef = useRef(null);
  const listenersRef = useRef(new Set());
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const intentionalDisconnectRef = useRef(false);
  const [status, setStatus] = useState('disconnected');
  const feedsRef = useRef([]);
  const extraFeedsRef = useRef(new Map());

  const broadcast = useCallback((message) => {
    listenersRef.current.forEach((callback) => {
      try {
        callback(message);
      } catch (err) {
        console.error('[WS] Error in listener:', err);
      }
    });
  }, []);

  const sendToServer = useCallback((action, payload) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      if (action === 'subscribe' && payload?.feeds) {
        // Azure Web PubSub uses joinGroup protocol — replace / with . in group names
        for (const feed of payload.feeds) {
          const group = feed.replace(/\//g, '.');
          console.log('[WS] joinGroup:', group);
          wsRef.current.send(JSON.stringify({ type: 'joinGroup', group }));
        }
      } else if (action === 'unsubscribe' && payload?.feeds) {
        for (const feed of payload.feeds) {
          const group = feed.replace(/\//g, '.');
          console.log('[WS] leaveGroup:', group);
          wsRef.current.send(JSON.stringify({ type: 'leaveGroup', group }));
        }
      } else {
        wsRef.current.send(JSON.stringify({ action, ...payload }));
      }
    } else {
      console.warn('[WS] Not connected, cannot send:', action, payload);
    }
  }, []);

  const connect = useCallback(async () => {
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) return;

    const token = getAuthToken();
    if (!token) return;

    intentionalDisconnectRef.current = false;
    setStatus('connecting');

    try {
      // Negotiate with Azure Web PubSub via our backend to get a WebSocket URL
      const negotiateRes = await apiFetch('/api/signalr/negotiate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!negotiateRes.ok) {
        console.error('[WS] Negotiate failed:', negotiateRes.status);
        setStatus('disconnected');
        scheduleReconnect();
        return;
      }

      const { url: wsUrl } = await negotiateRes.json();
      const ws = new WebSocket(wsUrl, 'json.webpubsub.azure.v1');

      ws.onopen = () => {
        console.log('[WS] Connected');
        setStatus('connected');
        reconnectAttemptRef.current = 0;

        const allFeeds = [
          ...feedsRef.current,
          ...extraFeedsRef.current.keys(),
        ];
        console.log('[WS] Joining initial feeds:', allFeeds);
        for (const feed of allFeeds) {
          const group = feed.replace(/\//g, '.');
          ws.send(JSON.stringify({ type: 'joinGroup', group }));
        }
      };

      ws.onmessage = (event) => {
        try {
          const raw = JSON.parse(event.data);

          // Azure Web PubSub wraps group messages: { type: "message", from: "group", group: "...", data: {...} }
          if (raw.type === 'message' && raw.from === 'group' && raw.data) {
            const message = typeof raw.data === 'string' ? JSON.parse(raw.data) : raw.data;
            // Convert group name back to feed format (. → /)
            if (message.feed) {
              message.feed = message.feed.replace(/\./g, '/');
            } else if (raw.group) {
              message.feed = raw.group.replace(/\./g, '/');
            }
            broadcast(message);
          } else if (raw.type === 'system') {
            // System messages (connected, joinGroup ack, etc.) — ignore
          } else {
            // Direct messages or unknown format — pass through
            broadcast(raw);
          }
        } catch (err) {
          console.error('[WS] Failed to parse message:', err);
        }
      };

      ws.onclose = () => {
        setStatus('disconnected');
        wsRef.current = null;

        if (!intentionalDisconnectRef.current) {
          scheduleReconnect();
        }
      };

      ws.onerror = () => {
        setStatus('disconnected');
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('[WS] Connection error:', err);
      setStatus('disconnected');
      scheduleReconnect();
    }
  }, [broadcast]);

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimerRef.current) return;

    const attempt = reconnectAttemptRef.current;
    const delay = Math.min(WS_RECONNECT_BASE_DELAY * Math.pow(2, attempt), WS_RECONNECT_MAX_DELAY);
    reconnectAttemptRef.current = attempt + 1;

    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      connect();
    }, delay);
  }, [connect]);

  const disconnect = useCallback(() => {
    intentionalDisconnectRef.current = true;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnect');
      wsRef.current = null;
    }
    setStatus('disconnected');
  }, []);

  useEffect(() => {
    if (user) {
      connect();
    } else {
      disconnect();
    }
    return () => disconnect();
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' &&
          (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN)) {
        reconnectAttemptRef.current = 0;
        connect();
      }
    };

    const handleOnline = () => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        reconnectAttemptRef.current = 0;
        connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [user, connect]);

  useEffect(() => {
    if (status !== 'connected' || !show?._id || !user?.id) return;

    const feeds = [
      `show/${show._id}`,
      `show/${show._id}/user/${user.id}`,
    ];
    feedsRef.current = feeds;

    sendToServer('subscribe', { feeds });

    const toastHandler = (message) => {
      if (message?.event !== 'user_notification') return;
      const data = message.data;
      if (!data?.message) return;

      const isExternal = data.link?.startsWith('http');
      toast(data.message, {
        description: data.description,
        duration: 5000,
        icon: data.icon ? <img src={data.icon} alt="" className="w-5 h-5 rounded" /> : undefined,
        action: data.link ? {
          label: 'View',
          onClick: () => {
            if (isExternal) {
              window.open(data.link, '_blank', 'noopener,noreferrer');
            } else {
              const basename = import.meta.env.BASE_URL.replace(/\/$/, '');
              const target = basename && data.link.startsWith('/') && !data.link.startsWith(basename + '/')
                ? `${basename}${data.link}`
                : data.link;
              window.location.href = target;
            }
          },
        } : undefined,
        cancel: {
          label: 'Dismiss',
          onClick: () => {},
        },
      });
    };

    listenersRef.current.add(toastHandler);

    return () => {
      listenersRef.current.delete(toastHandler);
      sendToServer('unsubscribe', { feeds: feedsRef.current });
      feedsRef.current = [];
    };
  }, [status, show?._id, user?.id, sendToServer]);

  const addListener = useCallback((callback) => {
    listenersRef.current.add(callback);
    return () => {
      listenersRef.current.delete(callback);
    };
  }, []);

  const subscribe = useCallback((feeds) => {
    const feedList = Array.isArray(feeds) ? feeds : [feeds];
    const map = extraFeedsRef.current;
    const newFeeds = [];

    for (const feed of feedList) {
      const count = map.get(feed) || 0;
      map.set(feed, count + 1);
      if (count === 0) newFeeds.push(feed);
    }

    if (newFeeds.length > 0) {
      sendToServer('subscribe', { feeds: newFeeds });
    }

    return () => {
      const removedFeeds = [];
      for (const feed of feedList) {
        const count = map.get(feed) || 0;
        if (count <= 1) {
          map.delete(feed);
          removedFeeds.push(feed);
        } else {
          map.set(feed, count - 1);
        }
      }
      if (removedFeeds.length > 0) {
        sendToServer('unsubscribe', { feeds: removedFeeds });
      }
    };
  }, [sendToServer]);

  const value = {
    status,
    addListener,
    subscribe,
    sendToServer,
    connect,
    disconnect,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocketContext() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
}
